import React, { useState } from "react";
import moment from "moment";
import { DateRangePicker, isInclusivelyBeforeDay } from "react-dates";
import styled from "styled-components";

import { weekDayMap } from "../../utils/weekConfig";

const ToText = styled.div`
  padding-top: 10px;
  font-size: ${({ theme }) => theme.dropdown.fontSize};
  color: ${({ theme }) => theme.font.pageText.colorMain};
`;

const DateRangePickerButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ isApply, theme }) =>
    isApply ? theme.button.primary.bgColor : theme.button.secondary.bgColor};
  border: 1px solid
    ${({ isApply, theme }) => !isApply && theme.button.secondary.borderColor};
  color: ${({ isApply, theme }) =>
    isApply
      ? theme.button.primary.textColor
      : theme.button.secondary.textColor};
  height: 36px;
  padding: 0px 10px;
  cursor: pointer;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  border-radius: ${({ theme }) => theme.button.borderRadius};
  margin: ${({ margin }) => margin || "0"};
  &:hover {
    background-color: ${({ isApply, theme }) =>
      isApply
        ? theme.button.primary.hoverBgColor
        : theme.button.secondary.hoverBgColor};
    color: ${({ theme }) => theme.button.secondary.hoverTextColor};
  }
`;

const CustomInputIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: "-2px" }}>
    <path d="M6.66675 1.66667V4.16667" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.3333 1.66667V4.16667" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2.91675 7.5H17.0834" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17.5 6.66667V14.1667C17.5 16.8334 16.1667 18.3334 13.1667 18.3334H6.83337C3.83337 18.3334 2.5 16.8334 2.5 14.1667V6.66667C2.5 4 3.83337 2.5 6.83337 2.5H13.1667C16.1667 2.5 17.5 4 17.5 6.66667Z" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.75 11.25H13.7575" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.0025 11.25H10.01" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.25 11.25H6.2575" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.75 14.5H13.7575" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.0025 14.5H10.01" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.25 14.5H6.2575" stroke="#353A46" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ArrowIcon = () => <ToText>to</ToText>;

const ReactWeekPicker = ({
  startDate,
  startDateId,
  endDate,
  endDateId,
  onDatesChange,
  disableCurrentAndFutureDates,
  disablePastDates,
  showFooter = true,
  onDateSelect,
  dateDisplayFormat = localStorage.getItem("date_format") || "DD/MM/YYYY",
  startDayOfWeek = localStorage.getItem("start_day_of_week" || "monday"),
  selectLastWeek,
  selectCurrentWeek,
}) => {
  const [focusedInput, setFocusedInput] = useState(null);

  const getCalendarInfo = (showFooter = true) => {
    return (
      <div className="date-range-footer">
        {showFooter && (
          <>
            <div style={{ display: "flex", alignItems: "center" }}>
              <DateRangePickerButton
                onClick={() => {
                  if (selectLastWeek) selectLastWeek();
                  setFocusedInput(null);
                }}
              >
                Last Week
              </DateRangePickerButton>
              <DateRangePickerButton
                margin="0 0 0 8px"
                onClick={() => {
                  if (selectCurrentWeek) selectCurrentWeek();
                  setFocusedInput(null);
                }}
              >
                This Week
              </DateRangePickerButton>
            </div>
          </>
        )}
      </div>
    );
  };

  const handleDatesChange = ({ startDate }) => {
    if (startDate) {
      const startOfWeek = moment(startDate)
        .startOf("week")
        .format("YYYY-MM-DD");
      const endOfWeek = moment(startDate).endOf("week").format("YYYY-MM-DD");
      onDatesChange({ startDate: startOfWeek, endDate: endOfWeek });
    } else {
      onDatesChange(null, null);
    }
  };

  return (
    <DateRangePicker
      startDate={startDate}
      startDateId={startDateId}
      endDate={endDate}
      endDateId={endDateId}
      onDatesChange={({ startDate, endDate }) =>
        handleDatesChange({ startDate, endDate })
      }
      focusedInput={focusedInput}
      onFocusChange={input => {
        if (input === "endDate") {
          focusedInput === "startDate"
            ? setFocusedInput(null)
            : setFocusedInput("startDate");
        } else {
          setFocusedInput(input);
        }
      }}
      showDefaultInputIcon={false}
      customInputIcon={<CustomInputIcon />}
      inputIconPosition="after"
      isOutsideRange={
        disableCurrentAndFutureDates
          ? day => !isInclusivelyBeforeDay(day, moment().subtract(1, "day"))
          : disablePastDates
          ? day => isInclusivelyBeforeDay(day, moment().subtract(1, "day"))
          : () => false
      }
      displayFormat={dateDisplayFormat}
      firstDayOfWeek={weekDayMap[startDayOfWeek]}
      renderCalendarInfo={() => getCalendarInfo(showFooter)}
      calendarInfoPosition="bottom"
      keepOpenOnDateSelect={showFooter}
      customArrowIcon={<ArrowIcon />}
      numberOfMonths={1}
      minimumNights={0}
      hideKeyboardShortcutsPanel
      readOnly
    />
  );
};

export default ReactWeekPicker;
